import {
  FinanceReportCategory,
  ReportModule,
  type FinanceReportEntry,
} from "@/features/reports/model/report-registry-types"

/**
 * The catalog's tab row.
 *
 * `All` and `Favorites` are VIEWS over every report rather than groups of their own: a report is never
 * "in" them, it is only shown there. Keeping them out of `ReportCatalogGroup` is what stops a curation file
 * ever having to name them.
 */
export enum ReportCatalogTab {
  All = "all",
  Favorites = "favorites",
}

/**
 * The family a report is filed under on the catalog: the pill it answers to, and the coloured tag on its row.
 *
 * Derived from the registry (module, category, data source) rather than declared per entry, so a new report
 * lands in the right pill without anyone remembering to say so. The slugs of the three original module tabs
 * are kept as they were (`finance`, `members`, `activities`), so an old bookmark still opens its tab.
 */
export enum ReportCatalogGroup {
  Finance = "finance",
  Funds = "funds",
  Members = "members",
  Activities = "activities",
  Accounting = "accounting",
}

/** A tab is either one of the two views or a report group slug. */
export type ReportCatalogTabValue = ReportCatalogTab | ReportCatalogGroup

/** Left-to-right order. Groups follow the two views, in the order a church meets them. */
export const REPORT_CATALOG_TABS: readonly ReportCatalogTabValue[] = [
  ReportCatalogTab.All,
  ReportCatalogTab.Favorites,
  ReportCatalogGroup.Finance,
  ReportCatalogGroup.Funds,
  ReportCatalogGroup.Members,
  ReportCatalogGroup.Activities,
  ReportCatalogGroup.Accounting,
]

/** The group pills only, in tab order. */
export const REPORT_CATALOG_GROUPS: readonly ReportCatalogGroup[] = REPORT_CATALOG_TABS.filter(
  (tab): tab is ReportCatalogGroup => tab !== ReportCatalogTab.All && tab !== ReportCatalogTab.Favorites,
)

const TAB_VALUES = new Set<string>(REPORT_CATALOG_TABS)

/** Categories that are the books themselves: statements, the chart and the reconciliation of accounts. */
const ACCOUNTING_CATEGORIES: ReadonlySet<FinanceReportCategory> = new Set([
  FinanceReportCategory.FinancialStatements,
  FinanceReportCategory.Audit,
])

/**
 * Which group a report belongs to.
 *
 * Every report reading the accounting endpoints (statements, trial balance, ledger, journal, chart) is
 * Accounting, whatever section it used to sit in: the ledger and the journal were filed under "Transactions"
 * and so appeared beside the engine's money reports as if they were the same kind of thing.
 */
export function reportCatalogGroup(entry: Pick<FinanceReportEntry, "module" | "category" | "dataSource">): ReportCatalogGroup {
  if (entry.module === ReportModule.Members) return ReportCatalogGroup.Members
  if (entry.module === ReportModule.Activities) return ReportCatalogGroup.Activities
  if (entry.dataSource.kind === "accounting" || ACCOUNTING_CATEGORIES.has(entry.category)) {
    return ReportCatalogGroup.Accounting
  }
  if (entry.category === FinanceReportCategory.Funds) return ReportCatalogGroup.Funds
  return ReportCatalogGroup.Finance
}

/**
 * Which tab a PATHNAME is on.
 *
 * Suffix-matched rather than parsed, like the other tabbed pages here: the catalog sits at `/:ws/reports`
 * and `/:ws/:branch/reports`, and the tail is the same in both. The tab is a real path segment, so it
 * survives a reload and a copied link.
 */
export function resolveReportCatalogTabFromPathname(pathname: string): ReportCatalogTabValue {
  const segments = pathname.replace(/\/+$/, "").split("/")
  const last = segments[segments.length - 1]
  // `/reports` itself is the All tab; anything after it names a tab.
  return last === "reports" ? ReportCatalogTab.All : resolveReportCatalogTab(last)
}

/**
 * Which tab a URL segment names.
 *
 * The tab is a REAL path segment (`/reports/members`), so it survives a reload and a copied link: the
 * repo's rule for tabbed pages. An unknown or absent segment falls back to `All` rather than 404ing: a
 * retired slug in someone's bookmark should land them in the catalog, not on an error.
 */
export function resolveReportCatalogTab(segment: string | undefined): ReportCatalogTabValue {
  if (!segment) return ReportCatalogTab.All
  return TAB_VALUES.has(segment) ? (segment as ReportCatalogTabValue) : ReportCatalogTab.All
}

/** The label key for a tab or a group tag. */
export function reportCatalogTabLabelKey(tab: ReportCatalogTabValue): string {
  return `finance.reports.modules.${tab}`
}

/** The reports a tab shows, from the reports this reader may see. Each report at most once. */
export function reportsForCatalogTab(
  entries: readonly FinanceReportEntry[],
  tab: ReportCatalogTabValue,
  favoriteIds: ReadonlySet<string>,
): FinanceReportEntry[] {
  const unique = uniqueReports(entries)
  if (tab === ReportCatalogTab.All) return unique
  if (tab === ReportCatalogTab.Favorites) return unique.filter((entry) => favoriteIds.has(entry.id))
  return unique.filter((entry) => reportCatalogGroup(entry) === tab)
}

/** First occurrence of each report id wins: a report is listed once, however many lists offered it. */
export function uniqueReports(entries: readonly FinanceReportEntry[]): FinanceReportEntry[] {
  const seen = new Set<string>()
  const out: FinanceReportEntry[] = []
  for (const entry of entries) {
    if (seen.has(entry.id)) continue
    seen.add(entry.id)
    out.push(entry)
  }
  return out
}
